import { MouseEvent, useCallback } from 'react';

import {
  useAggAsk,
  useAggBid,
  useMyAggAsks,
  useMyAggBids,
  AggGrfqQuote,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/quotes-contexts';
import { useQuoteBuilderModalMethods } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/modal-context';
import {
  AskPriceButton,
  BidPriceButton,
  EmptyPrice,
  QtyButton,
} from '#/components/rfq-common/ActionButtons';
import { dividerCss } from '#/components/rfq-common/TableRow';

import { useRfqMarkPrice } from '@paradigm/features/src/unified-rfqs/ui/rfq-websocket/mark-price-context';
import {
  useGrfqRfq,
  useRfqContext,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { isDatedRfqLegs } from '@paradigm/features/src/unified-rfqs/ui/rfqs/legs-functions';
import PriceButtonWithMark from '#/components/UnifiedRfqs/one-click-trade/PriceButtonWithMark';
import checkForOwnQuote from '@paradigm/utils/checkForOwnQuote';
import KillQuoteButton from '#/components/UnifiedRfqs/rfqs/action/KillQuoteButton';
import { BaseRfqAction } from '#/components/UnifiedRfqs/rfqs/action/BaseRfqAction';
import { useGrfqService } from '@paradigm/features/src/unified-rfqs/contexts/grfqs';
import { useFormattedGrfqQuotePrice } from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import {
  useUMPlatformCreationDisabled,
  useUMPlatformKillDisabled,
} from '@paradigm/features/src/platform-state/contexts/providers';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { useObQuoteMatching } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-matching/ob-quote-matching-provider';

interface Props {
  readonly isSecondary?: boolean;
}

export default function RfqAction({ isSecondary = false }: Props) {
  const isPlatformCreationDisabled = useUMPlatformCreationDisabled();
  const isPlatformKillDisabled = useUMPlatformKillDisabled();
  const bboView = useBboView();

  const { getBboOffset, markPriceIv } = bboView;
  const mark = bboView.markPrice.formatted;

  const bid = useAggBid();
  const ask = useAggAsk();
  const { isMatchingQuote } = useObQuoteMatching();

  const myBid = checkForOwnQuote(useAggBid(), useMyAggBids());
  const myAsk = checkForOwnQuote(useAggAsk(), useMyAggAsks());

  const hasBid = bid != null;
  const hasAsk = ask != null;
  const bidFlashKey =
    hasBid && isMatchingQuote(bid) ? bid.id.toString() : undefined;
  const askFlashKey =
    hasAsk && isMatchingQuote(ask) ? ask.id.toString() : undefined;

  const bidQty = bid?.remaining_quantity ?? '0';
  const askQty = ask?.remaining_quantity ?? '0';

  const bidKillButton =
    myBid != null ? (
      <KillQuoteButton
        disabled={isPlatformKillDisabled}
        side="BUY"
        price={myBid.price}
        title="kill my bid"
      />
    ) : null;
  const askKillButton =
    myAsk != null ? (
      <KillQuoteButton
        disabled={isPlatformKillDisabled}
        side="SELL"
        price={myAsk.price}
        title="kill my ask"
      />
    ) : null;
  if (isSecondary) {
    return (
      <BaseRfqAction
        css={dividerCss}
        bidFlashKey={bidFlashKey}
        bidQty={
          hasBid ? (
            <RfqQtyButton
              disabled={isPlatformCreationDisabled}
              quantity={bidQty}
              side="BUY"
            />
          ) : (
            '–'
          )
        }
        bidKill={bidKillButton}
        bidPrice={
          <RfqPriceButton
            disabled={isPlatformCreationDisabled}
            side="BUY"
            quote={bid}
            isSecondary
          />
        }
        askFlashKey={askFlashKey}
        askPrice={
          <RfqPriceButton
            disabled={isPlatformCreationDisabled}
            side="SELL"
            quote={ask}
            isSecondary
          />
        }
        askKill={askKillButton}
        askQty={
          hasAsk ? (
            <RfqQtyButton
              disabled={isPlatformCreationDisabled}
              quantity={askQty}
              side="SELL"
            />
          ) : (
            '–'
          )
        }
      />
    );
  }

  const { offsetBid, offsetAsk, isBetterThanScreenBid, isBetterThanScreenAsk } =
    getBboOffset({
      askPrice: ask?.price,
      bidPrice: bid?.price,
    });

  return (
    <BaseRfqAction
      markIv={markPriceIv.formatted}
      bidFlashKey={bidFlashKey}
      bidQty={
        hasBid ? (
          <RfqQtyButton
            disabled={isPlatformCreationDisabled}
            quantity={bidQty}
            side="BUY"
          />
        ) : (
          '–'
        )
      }
      bidKill={bidKillButton}
      bidPrice={
        <RfqPriceButton
          disabled={isPlatformCreationDisabled}
          side="BUY"
          quote={bid}
        />
      }
      mark={mark}
      askFlashKey={askFlashKey}
      askPrice={
        <RfqPriceButton
          disabled={isPlatformCreationDisabled}
          side="SELL"
          quote={ask}
        />
      }
      askKill={askKillButton}
      askQty={
        hasAsk ? (
          <RfqQtyButton
            disabled={isPlatformCreationDisabled}
            quantity={askQty}
            side="SELL"
          />
        ) : (
          '–'
        )
      }
      offsetBid={offsetBid}
      offsetAsk={offsetAsk}
      isBetterThanScreenBestBid={isBetterThanScreenBid}
      isBetterThanScreenBestAsk={isBetterThanScreenAsk}
    />
  );
}

interface QuotePriceButtonProps {
  readonly quote: AggGrfqQuote | undefined;
  readonly side: 'BUY' | 'SELL';
  readonly className?: string;
  readonly isSecondary?: boolean;
  readonly disabled: boolean;
}

function RfqPriceButton({
  quote,
  side,
  className,
  isSecondary = false,
  disabled = false,
  ...props
}: QuotePriceButtonProps) {
  const onClickHandler = useHandlePriceClick(quote, side);
  const { isMarkOrBetter } = useRfqMarkPrice();
  const { product_codes = [] } = useGrfqRfq();
  // quote.price is served net of the hedge (matches the mark/border).
  const formattedPrice = useFormattedGrfqQuotePrice(quote);

  if (quote == null) {
    return <EmptyPrice disabled={disabled} onClick={onClickHandler} />;
  }

  if (!isSecondary) {
    return (
      <PriceButtonWithMark
        onClick={onClickHandler}
        formattedPrice={formattedPrice}
        price={quote.price}
        side={side}
        disabled={disabled}
        isMarkOrBetter={isMarkOrBetter}
        productCode={product_codes[0]}
      />
    );
  }

  const Button = side === 'SELL' ? AskPriceButton : BidPriceButton;

  return (
    <Button
      {...props}
      disabled={disabled}
      onClick={onClickHandler}
      price={formattedPrice}
      className={className}
    />
  );
}

function useHandlePriceClick(
  aggQuote: AggGrfqQuote | undefined,
  side: 'BUY' | 'SELL',
) {
  const { rfqId } = useRfqContext();
  const quoteBuilderModal = useQuoteBuilderModalMethods();
  const grfqService = useGrfqService();
  const track = useTrackAction();

  const handlePriceClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      track('view quote builder', { auctionType: 'OB' });

      let clickedQuote = null;
      const quoteId = aggQuote?.id;
      if (quoteId != null) {
        clickedQuote = grfqService.selectRfqQuoteById(rfqId, quoteId) ?? null;
      }

      event.stopPropagation();
      quoteBuilderModal.open({ rfqId, clickedQuote, side });
    },
    [track, aggQuote?.id, quoteBuilderModal, rfqId, side, grfqService],
  );

  return handlePriceClick;
}

interface QtyButtonProps {
  readonly quantity: string;
  readonly side: 'BUY' | 'SELL';
  readonly disabled: boolean;
}

function RfqQtyButton({ quantity, side, disabled = false }: QtyButtonProps) {
  const rfq = useGrfqRfq();
  const quoteBuilderModal = useQuoteBuilderModalMethods();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    quoteBuilderModal.open({ rfqId: rfq.id, side, clickedQuote: null });
  };

  return (
    <QtyButton
      onClick={handleClick}
      productCode={rfq.product_codes[0]}
      quantity={quantity}
      disabled={disabled}
      isDated={isDatedRfqLegs(rfq.legs, rfq.product_codes[0])}
    />
  );
}
